import type MDBSemi from '@/types/ferme/semi';
import type MDBVegcoBucket from '@/types/vegco/bucket';
import { toLocaleDateString, stringArrayEqual, roundTo } from '@/utils/helpers';
import { unitésParBoîte } from '@/utils/helpers-vegco';

export declare interface HistoriqueChangementEntrée {
  champ: string
  anciènneValeur: any
  nouvelleValeur: any
}

export declare interface HistoriqueChangement {
  objectId: string
  modifiéPar: string
  dateModification: string
  résumé: string
  entrées: HistoriqueChangementEntrée[]
}

function compareDates(top: HistoriqueChangement, dateAncienne: string, dateNouvelle: string, champ: string, résumé: string) {
  if (dateAncienne !== dateNouvelle) {
    top.entrées.push({ champ, anciènneValeur: dateAncienne, nouvelleValeur: dateNouvelle })
    top.résumé += `[${résumé}: ${toLocaleDateString(dateAncienne)} -> ${toLocaleDateString(dateNouvelle)}]  `
  }
}

function compareString(top: HistoriqueChangement, ancienne: string, nouvelle: string, champ: string, résumé: string) {
  if (ancienne !== nouvelle) {
    top.entrées.push({ champ, anciènneValeur: ancienne, nouvelleValeur: nouvelle })
    top.résumé += `[${résumé}: ${ancienne} -> ${nouvelle}]  `
  }
}

function compareStringArray(top: HistoriqueChangement, ancienne: string[], nouvelle: string[], champ: string, résumé: string) {
  if (stringArrayEqual(ancienne, nouvelle)) {
    return
  }

  top.entrées.push({ champ, anciènneValeur: ancienne, nouvelleValeur: nouvelle })
  top.résumé += `[${résumé}: ${ancienne} -> ${nouvelle}]  `
}

function compareNumber(top: HistoriqueChangement, ancienne: number, nouvelle: number, champ: string, résumé: string) {
  if (ancienne !== nouvelle) {
    top.entrées.push({ champ, anciènneValeur: ancienne, nouvelleValeur: nouvelle })
    top.résumé += `[${résumé}: ${ancienne} -> ${nouvelle}]  `
  }
}

function compareBoolean(top: HistoriqueChangement, ancienne: boolean, nouvelle: boolean, champ: string, résumé: string) {
  if (ancienne !== nouvelle) {
    top.entrées.push({ champ, anciènneValeur: ancienne, nouvelleValeur: nouvelle })
    top.résumé += `[${résumé}: ${ancienne} -> ${nouvelle}]  `
  }
}

export function getBucketHistoriqueChangements(buckets: MDBVegcoBucket[]): HistoriqueChangement[] {
  const unités = unitésParBoîte(buckets[0]?.culture)
  const historiqueChangements: HistoriqueChangement[] = []
  for (let i = 0; i < buckets.length; i++) {
    const bucket = buckets[i]
    const top: HistoriqueChangement = {
      objectId: bucket.id,
      modifiéPar: bucket.modifiéPar,
      dateModification: bucket.valideDu,
      résumé: '',
      entrées: []
    }

    if (i == buckets.length - 1) { top.résumé = 'Création' }
    else if (bucket.valideDu == bucket.valideAu) { top.résumé = 'Supprimé' }
    else {
      top.résumé = 'Modifications: '
      const previous = buckets[i + 1]

      compareString(top, previous.bucketParentId, bucket.bucketParentId, 'bucketParentId', 'Bucket parent ID')
      compareString(top, previous.semiId, bucket.semiId, 'semiId', 'Semi ID')
      compareString(top, previous.numéroSemi, bucket.numéroSemi, 'numéroSemi', 'Numéro Semi')
      compareBoolean(top, previous.semiFermé, bucket.semiFermé, 'semiFermé', 'Semi fermé')
      compareString(top, previous.commandeVenteId, bucket.commandeVenteId, 'commandeVenteId', 'Commande Vente ID')
      compareString(top, previous.noCommandeAchat, bucket.noCommandeAchat, 'noCommandeAchat', 'No commande achat')
      compareDates(top, previous.date, bucket.date, 'date', 'Date')
      compareDates(top, previous.datePublication, bucket.datePublication, 'datePublication', 'Date Publication')
      compareBoolean(top, previous.dateChangéeManuellement, bucket.dateChangéeManuellement, 'dateChangéeManuellement', 'Date changée manuellement')
      compareString(top, previous.codeProduit, bucket.codeProduit, 'codeProduit', 'Code produit')
      compareString(top, previous.culture, bucket.culture, 'culture', 'Culture')
      compareString(top, previous.variété, bucket.variété, 'variété', 'Variété')
      compareString(top, previous.localisation, bucket.localisation, 'localisation', 'Localisation')
      compareNumber(top, previous.priorité, bucket.priorité, 'priorité', 'Priorité')
      compareNumber(top, roundTo(previous.quantité / unités, 0), roundTo(bucket.quantité / unités, 0), 'quantité', 'Quantité')
      compareNumber(top, roundTo(previous.quantitéConfirmée / unités, 0), roundTo(bucket.quantitéConfirmée / unités, 0), 'quantitéConfirmée', 'Quantité confirmée')
      compareBoolean(top, previous.quantitéChangéeManuellement, bucket.quantitéChangéeManuellement, 'quantitéChangéeManuellement', 'Quantité changée manuellement')
      compareNumber(top, previous.quantitéParPalette, bucket.quantitéParPalette, 'quantitéParPalette', 'Quantité par palette')
      compareBoolean(top, previous.estPrévision, bucket.estPrévision, 'estPrévision', 'Est prévision')
      compareString(top, previous.commentaires, bucket.commentaires, 'commentaires', 'Commentaires')
      compareStringArray(top, previous.balises, bucket.balises, 'balises', 'Balises')
    }

    historiqueChangements.push(top)
  }

  return historiqueChangements
}

/*export function getVegcoRécolteHistoriqueChangements(récoltes: MDBVegcoRécolte[]): HistoriqueChangement[] {
  const historiqueChangements: HistoriqueChangement[] = []
  for (let i = 0; i < récoltes.length; i++) {
    const récolte = récoltes[i]
    const top: HistoriqueChangement = {
      objectId: récolte.id,
      modifiéPar: récolte.modifiéPar,
      dateModification: récolte.valideDu,
      résumé: '',
      entrées: []
    }

    if (i == récoltes.length - 1) { top.résumé = 'Création' }
    else if (récolte.valideDu == récolte.valideAu) { top.résumé = 'Supprimé' }
    else {
      top.résumé = 'Modifications: '
      const previous = récoltes[i + 1]

      compareDates(top, previous.datePrévue, récolte.datePrévue, 'datePrévue', 'Date prévue')
      compareNumber(top, previous.quantitéPrévue, récolte.quantitéPrévue, 'quantitéPrévue', 'Quantité prévue')
      compareNumber(top, previous.quantitéRéelle, récolte.quantitéRéelle, 'quantitéRéelle', 'Quantité réelle')
      compareString(top, previous.commentaires, récolte.commentaires, 'commentaires', 'Commentaires')
      compareStringArray(top, previous.balises, récolte.balises, 'balises', 'Balises')
    }

    historiqueChangements.push(top)
  }

  return historiqueChangements
}*/

export function getSemiHistoriqueChangements(semis: MDBSemi[]): HistoriqueChangement[] {
  const historiqueChangements: HistoriqueChangement[] = []
  for (let i = 0; i < semis.length; i++) {
    const semi = semis[i]
    const top: HistoriqueChangement = {
      objectId: semi.id,
      modifiéPar: semi.modifiéPar,
      dateModification: semi.valideDu,
      résumé: '',
      entrées: []
    }

    if (i == semis.length - 1) { top.résumé = 'Création' }
    else if (semi.valideDu == semi.valideAu) { top.résumé = 'Supprimé' }
    else {
      top.résumé = 'Modifications: '
      const previous = semis[i + 1]

      compareString(top, previous.siteId, semi.siteId, 'siteId', 'Site ID')
      compareNumber(top, previous.année, semi.année, 'année', 'Année')
      compareDates(top, previous.dateDu, semi.dateDu, 'dateDu', 'Date')
      compareDates(top, previous.dateAu, semi.dateAu, 'dateAu', 'Date à')
      compareDates(top, previous.dateRécolte, semi.dateRécolte, 'dateRécolte', 'Date récolte')
      compareDates(top, previous.dateEnSerre, semi.dateEnSerre, 'dateEnSerre', 'Date en serre')
      compareDates(top, previous.dateVérification, semi.dateVérification, 'dateVérification', 'Date de vérification')
      compareString(top, previous.farmitId, semi.farmitId, 'farmitId', 'Farmit ID')
      compareString(top, previous.numéro, semi.numéro, 'numéro', 'Numéro')
      compareString(top, previous.champ, semi.champ, 'champ', 'Champ')
      compareString(top, previous.lot, semi.lot, 'lot', 'Lot')
      compareString(top, previous.culture, semi.culture, 'culture', 'Culture')
      compareString(top, previous.variété, semi.variété, 'variété', 'Variété')
      compareString(top, previous.typePlantation, semi.typePlantation, 'typePlantation', 'Type plantation')
      compareNumber(top, previous.plancheDe, semi.plancheDe, 'plancheDe', 'Planche de')
      compareNumber(top, previous.plancheÀ, semi.plancheÀ, 'plancheÀ', 'Planche à')
      compareNumber(top, previous.longueur, semi.longueur, 'longueur', 'Longueur')
      compareNumber(top, previous.superficie, semi.superficie, 'superficie', 'Superficie')
      compareNumber(top, previous.superficieManuelle, semi.superficieManuelle, 'superficieManuelle', 'Superficie manuelle')
      compareNumber(top, previous.densité, semi.densité, 'densité', 'Densité')
      compareNumber(top, previous.densitéPosition, semi.densitéPosition, 'densitéPosition', 'Densité position')
      compareNumber(top, previous.grainesParPied, semi.grainesParPied, 'grainesParPied', 'Graines par pied')
      compareNumber(top, previous.vitesse, semi.vitesse, 'vitesse', 'Vitesse')
      compareString(top, previous.gear, semi.gear, 'gear', 'Gear')
      compareString(top, previous.commentaires, semi.commentaires, 'commentaires', 'Commentaires')
      compareString(top, previous.commentairesFarmit, semi.commentairesFarmit, 'commentairesFarmit', 'Commentaires Farmit')
      compareDates(top, previous.dateRécoltePrévue, semi.dateRécoltePrévue, 'dateRécoltePrévue', 'Date récolte prévue')
      compareDates(top, previous.dateRécoltePrévueManuelle, semi.dateRécoltePrévueManuelle, 'dateRécoltePrévueManuelle', 'Date récolte prévue manuelle')
      compareNumber(top, previous.rendementPrévu, semi.rendementPrévu, 'rendementPrévu', 'Rendement prévu')
      compareBoolean(top, previous.fermé, semi.fermé, 'fermé', 'Fermé')

      compareString(top, previous.récoltesCibleSemiId, semi.récoltesCibleSemiId, 'récoltesCibleSemiId', 'Récoltes cible semi ID')

      if (previous.récoltes.length !== semi.récoltes.length) {
        top.entrées.push({ champ: 'récoltes', anciènneValeur: previous.récoltes.length, nouvelleValeur: semi.récoltes.length })
        top.résumé += `[Récoltes: ${previous.récoltes.length} -> ${semi.récoltes.length}]  `
      }

      if (previous.degréJour == null && semi.degréJour !== null) {
        top.entrées.push({ champ: 'degréJour', anciènneValeur: previous.degréJour, nouvelleValeur: semi.degréJour })
        top.résumé += `[Création Degré jour]  `
      }
      else if (previous.degréJour !== null && semi.degréJour != null) {
        compareBoolean(top, previous.degréJour.complété, semi.degréJour.complété, 'degréJour.complété', 'Degré jour complété')
        compareNumber(top, previous.degréJour.djNécessaireCalculé, semi.degréJour.djNécessaireCalculé, 'degréJour.djNécessaireCalculé', 'Degré jour nécessaire calculé')
        compareNumber(top, previous.degréJour.djNécessaireManuel, semi.degréJour.djNécessaireManuel, 'degréJour.djNécessaireManuel', 'Degré jour nécessaire manuel')
        compareDates(top, previous.degréJour.dateRécoltePrévueInitiale, semi.degréJour.dateRécoltePrévueInitiale, 'degréJour.dateRécoltePrévueInitiale', 'Degré jour date récolte prévue initiale')
        compareDates(top, previous.degréJour.dateRécoltePrévue, semi.degréJour.dateRécoltePrévue, 'degréJour.dateRécoltePrévue', 'Degré jour date récolte prévue')
        compareNumber(top, previous.degréJour.djRéelAccumulé, semi.degréJour.djRéelAccumulé, 'degréJour.djRéelAccumulé', 'Degré jour réel accumulé')
      }
    }

    historiqueChangements.push(top)
  }

  return historiqueChangements
}
